import type { ExpandedState } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import cx from 'classnames';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useBulkSelectionContext } from '@zen/DesignSystem/BulkSelectionContext';
import { HEADER_HEIGHT_IN_REM } from '@zen/Layout';
import type { PageInfo, SortInput } from '@zen/types';
import { createTrackingLabelAttribute, createTrackingParentAttribute } from '@zen/utils/tracking';
import type { Optional } from '@zen/utils/typescript';

import NoResults from '../NoResults';
import { ExpandIcon } from './components/ExpandIcon';
import TableColumnActionIndicator from './components/TableColumnActionIndicator';
import TableFooter from './components/TableFooter';
import TableGroupHeader from './components/TableGroupHeader';
import TableHeader from './components/TableHeader';
import TableHeaderCell from './components/TableHeaderCell';
import TableRow from './components/TableRow';
import TableTotal from './components/TableTotal';
import { addExpandColumn, areColumnKeysUnique, hasExpandableRow, isMultiBulkSelectionInColumns } from './helpers';
import { useColumnOrder } from './hooks/useColumnOrder';
import { useColumnWidths } from './hooks/useColumnWidths';
import { useFixedColumns } from './hooks/useFixedColumns';
import { useScrollShadows } from './hooks/useScrollShadows';
import { useTableColumns } from './hooks/useTableColumns';
import { useTableData } from './hooks/useTableData';
import { useTableDragDrop } from './hooks/useTableDragDrop';
import type { GroupBucket } from './hooks/useTableGrouping';
import { useTableGrouping } from './hooks/useTableGrouping';
import { useTableInstance } from './hooks/useTableInstance';
import { useTableResize } from './hooks/useTableResize';
import { useTableScroll } from './hooks/useTableScroll';
import { useTableSort } from './hooks/useTableSort';
import type { GroupByValue, GroupHeaderMeta, TableColumn, TableRowData, TanStackColumnMeta, TotalCountConfig } from './types';

// Stable empty array to use as default for hiddenColumns to prevent infinite re-renders
const EMPTY_HIDDEN_COLUMNS: string[] = [];

interface Props<T extends TableRowData<K>, K extends {} = T> {
  actions?: ReactNode;
  additionalActions?: ReactNode;
  className?: string;
  columns: TableColumn<T>[];
  data: T[];
  emptyText?: string;
  error?: boolean;
  groupBy?: (record: T) => GroupByValue;
  hiddenColumns?: string[];
  initialCollapsedGroups?: string[];
  isDraggingEnabled?: boolean;
  isGroupCollapsible?: boolean;
  loading?: boolean;
  onOrderChange?: (order: SortInput) => void;
  order?: SortInput;
  paginationInfo?: PageInfo;
  persistColumnWidths?: boolean;
  renderExpandedRow?: (record: T, index: number) => ReactNode;
  renderGroupHeader?: (meta: GroupHeaderMeta<T>) => ReactNode;
  rowKey?: string;
  stickyHeaderOffset?: number;
  tableId: string;
  tagline?: ReactNode;
  title?: ReactNode;
  titleClassNames?: string;
  totalCount?: ReactNode;
  totalCountConfig?: TotalCountConfig;
}

const Table = <T extends TableRowData<K>, K extends {} = T>(props: Props<T, K>): ReactElement => {
  const {
    actions,
    additionalActions,
    className,
    columns: columnConfiguration,
    data,
    groupBy,
    renderGroupHeader,
    isGroupCollapsible = false,
    initialCollapsedGroups,
    renderExpandedRow,
    emptyText = "We can't find any results",
    hiddenColumns: hiddenColumnsProperty = EMPTY_HIDDEN_COLUMNS,
    isDraggingEnabled = false,
    loading = false,
    totalCount = null,
    paginationInfo,
    onOrderChange,
    error,
    order,
    rowKey,
    stickyHeaderOffset = HEADER_HEIGHT_IN_REM,
    tableId,
    tagline,
    title,
    titleClassNames,
    totalCountConfig,
    persistColumnWidths
  } = props;

  // Ensure hiddenColumns is always an array (URL params might return a string)
  const hiddenColumns = Array.isArray(hiddenColumnsProperty) ? hiddenColumnsProperty : EMPTY_HIDDEN_COLUMNS;

  const [expanded, setExpanded] = useState<ExpandedState>({});
  const expandableTable = hasExpandableRow(data);
  const hasCustomExpandedRow = Boolean(renderExpandedRow);

  // Add expand column when there are rows with nestedRowItems - memoize to prevent infinite re-renders
  const columns = useMemo(
    () => (expandableTable ? addExpandColumn(columnConfiguration) : columnConfiguration),
    [expandableTable, columnConfiguration]
  );

  // Safeguard for bad column configuration where keys are not unique
  if (!areColumnKeysUnique(columns)) {
    throw new Error(`Each column key for table ${tableId} must be unique`);
  }

  if (isMultiBulkSelectionInColumns(columns)) {
    throw new Error('Table can contain only one column with bulk assignment enabled');
  }

  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement>(null);

  // Scroll position (preserved across sort within the same mount) and column width persistence
  const [pageScroll, setPageScroll] = useTableScroll();
  const [savedColumnWidths, setSavedColumnWidths] = useColumnWidths(tableId, persistColumnWidths);

  // Column order management
  const columnOrderInput = useMemo(() => columns.map(({ fixed, key }) => ({ fixed, key })), [columns]);
  const { columnOrder, setColumnOrder } = useColumnOrder(tableId, columnOrderInput);

  // Column computations (mapping, visibility, ordering, TanStack adaptation)
  const { columnVisibility, visibleColumns, tanstackColumns } = useTableColumns<T, K>({
    columns,
    columnOrder,
    data,
    hiddenColumns
  });

  // Resize handling
  const { widths, columnSizing, setColumnSizing, handleResizeEnd } = useTableResize({
    columns,
    savedColumnWidths,
    setSavedColumnWidths,
    tableWrapperRef,
    loading,
    visibleColumns
  });

  // Drag and drop handling
  const { isMarkerVisible, markerLeftOffset, handleColumnDraggedOver, handleColumnDragEnd, handleColumnDropped } =
    useTableDragDrop({ columnOrder, setColumnOrder });

  // Sort handling
  const { handleOrderChange } = useTableSort({
    onOrderChange,
    tableContentRef,
    setPageScroll
  });

  // Data preparation (loading state, expandable attributes)
  const { displayColumns, displayData } = useTableData<T, K>({
    data,
    loading,
    rowKey,
    tanstackColumns
  });

  // TanStack table instance
  const { getHeaderGroups, rows } = useTableInstance<T, K>({
    data: displayData,
    columns: displayColumns,
    expanded,
    columnSizing,
    columnVisibility,
    columnOrder,
    hasCustomExpandedRow,
    onExpandedChange: setExpanded,
    onColumnSizingChange: setColumnSizing
  });

  // Bucket rows into collapsible groups when groupBy is provided
  const { groupedBuckets, isGrouped, toggleGroupCollapse } = useTableGrouping({
    groupBy,
    initialCollapsedGroups,
    rows
  });

  // Calculate fixed column offsets for sticky positioning (pass ref for DOM measurement)
  const { offsets: fixedColumnOffsets } = useFixedColumns(visibleColumns, widths, tableWrapperRef);

  // Horizontal scroll shadow indicators
  const { showLeftShadow, showRightShadow } = useScrollShadows({ scrollContainerRef: tableContentRef });

  // Track the outer scroll container's clientWidth so nested content (expanded rows) can size itself to the visible viewport rather than the outer table's full width.
  const [viewportWidth, setViewportWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const element = tableContentRef.current;

    if (!element) {
      return;
    }

    setViewportWidth(element.clientWidth);
    const observer: ResizeObserver = new ResizeObserver(() => setViewportWidth(element.clientWidth));

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  // Bulk selection state for row highlighting
  const { getItems } = useBulkSelectionContext();
  const selectedIds = getItems(tableId);

  // Restore scroll position after sort
  useLayoutEffect(() => {
    if (tableContentRef.current && pageScroll) {
      tableContentRef.current.scrollLeft = pageScroll;
    }
  }, [pageScroll]);

  const tableTotals: ReactNode = totalCountConfig ? <TableTotal totalCountConfig={totalCountConfig} /> : totalCount;
  const showTableHeader: boolean = !!title || !!tagline || !!actions;
  const showTableFooter: boolean = Boolean(!!paginationInfo?.hasNextPage || !!paginationInfo?.hasPreviousPage || tableTotals);

  const wrapperClassNames: string = cx(
    'rounded-sm border border-grey-lighter border-solid divide-y divide-solid divide-grey-lighter overflow-hidden',
    loading ? 'js-table-data-loading' : '',
    className,
    tableId
  );

  const renderTableBody = (): ReactNode => {
    const hasNoRows: boolean = rows.length === 0 && !loading;

    if (hasNoRows) {
      return (
        <tr>
          <td className="text-center py-8" colSpan={displayColumns.length}>
            {emptyText}
          </td>
        </tr>
      );
    }

    type TableRowInstance = (typeof rows)[number];

    const renderRow = (row: TableRowInstance, rowIndex: number): ReactNode => {
      const rowId: Optional<string> = 'id' in row.original && typeof row.original.id === 'string' ? row.original.id : undefined;
      const isSelected: boolean = Boolean(rowId && selectedIds.includes(rowId));

      return (
        <TableRow
          key={row.id}
          fixedColumnOffsets={fixedColumnOffsets}
          isNested={row.depth > 0}
          isSelected={isSelected}
          renderExpandIcon={(expandableRow: TableRowInstance): ReactNode => <ExpandIcon row={expandableRow} />}
          renderExpandedRow={renderExpandedRow}
          row={row}
          rowIndex={rowIndex}
          tableId={tableId}
          totalRows={rows.length}
          widths={widths}
        />
      );
    };

    if (!isGrouped) {
      return rows.map(renderRow);
    }

    // Grouped rows keep their index in the ungrouped list, because row spans and renderExpandedRow are keyed on it
    const rowIndexById: Map<string, number> = new Map(
      rows.map((row: TableRowInstance, rowIndex: number): [string, number] => [row.id, rowIndex])
    );

    const renderGroupSection = (bucket: GroupBucket<T, TableRowInstance>): ReactNode => {
      const { groupKey, groupLabel, isCollapsed, items, rows: groupRows } = bucket;

      const handleToggleCollapse = (): void => toggleGroupCollapse(groupKey);

      const groupMeta: GroupHeaderMeta<T> = {
        groupKey,
        isCollapsed,
        itemCount: items.length,
        items,
        label: groupLabel,
        toggleCollapse: handleToggleCollapse
      };

      const renderGroupHeaderContent = (): ReactNode => {
        if (renderGroupHeader) {
          return renderGroupHeader(groupMeta);
        }

        return (
          <TableGroupHeader
            groupKey={groupKey}
            isCollapsed={isCollapsed}
            isCollapsible={isGroupCollapsible}
            label={groupLabel}
            onToggleCollapse={handleToggleCollapse}
          />
        );
      };

      const renderGroupRow = (row: TableRowInstance): ReactNode => {
        const rowIndex: Optional<number> = rowIndexById.get(row.id);

        if (rowIndex === undefined) return null;

        return renderRow(row, rowIndex);
      };

      return (
        <Fragment key={`group-section-${groupKey}`}>
          <tr
            className="bg-grey-lighter border-y border-solid border-grey-lighter select-none"
            data-testid={`table-group-header-${groupKey}`}
          >
            <td className="px-6 py-2" colSpan={displayColumns.length}>
              {renderGroupHeaderContent()}
            </td>
          </tr>
          {!isCollapsed && groupRows.map(renderGroupRow)}
        </Fragment>
      );
    };

    return groupedBuckets.map(renderGroupSection);
  };

  const renderTable = () => {
    if (error) {
      return <NoResults headline="Something went wrong" isCentered={false} tagline="Please try again later." />;
    }

    const headerGroups = getHeaderGroups();

    const wrapperStyle: CSSProperties = {
      '--zen-fixed-left-shadow': showLeftShadow ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
      '--zen-fixed-right-shadow': showRightShadow ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
      '--zen-viewport-width': viewportWidth ? `${viewportWidth}px` : '100%'
    } as CSSProperties;

    return (
      <DndProvider backend={HTML5Backend}>
        <div ref={tableWrapperRef} className="relative box-border" data-testid="table-wrapper" style={wrapperStyle}>
          <div ref={tableContentRef} className="overflow-x-auto overscroll-x-contain zen-table-content">
            <table
              className="border-spacing-0 w-full"
              data-component="table"
              data-testid="table"
              {...createTrackingParentAttribute('table')}
              {...createTrackingLabelAttribute(tableId, { ignoreContent: true })}
            >
              <thead className="bg-grey-lightest">
                {headerGroups.map((headerGroup) => {
                  type HeaderItem = (typeof headerGroup.headers)[number];

                  const getHeaderMeta = (header: HeaderItem): TanStackColumnMeta<T> | undefined =>
                    header.column.columnDef.meta as TanStackColumnMeta<T> | undefined;

                  const lastLeftFixedHeaderIndex: number = headerGroup.headers.reduce<number>(
                    (lastIndex: number, header: HeaderItem, index: number) =>
                      getHeaderMeta(header)?.fixed === 'left' ? index : lastIndex,
                    -1
                  );
                  const firstRightFixedHeaderIndex: number = headerGroup.headers.findIndex(
                    (header: HeaderItem) => getHeaderMeta(header)?.fixed === 'right'
                  );

                  return (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => {
                        const meta = header.column.columnDef.meta as TanStackColumnMeta<T> | undefined;
                        const columnKey = meta?.columnKey || header.id;
                        const isLastColumn = index === headerGroup.headers.length - 1;
                        const fixedDirection = meta?.fixed;
                        const fixedOffset = fixedDirection ? fixedColumnOffsets[columnKey] || 0 : 0;
                        const isFixedBoundary: boolean =
                          (fixedDirection === 'left' && index === lastLeftFixedHeaderIndex) ||
                          (fixedDirection === 'right' && index === firstRightFixedHeaderIndex);

                        return (
                          <TableHeaderCell
                            key={header.id}
                            columnKey={columnKey}
                            draggable={!fixedDirection && isDraggingEnabled}
                            fixed={fixedDirection}
                            fixedOffset={fixedOffset}
                            header={header}
                            headerOffset={stickyHeaderOffset}
                            isFixedBoundary={isFixedBoundary}
                            loading={loading}
                            offsetElementRef={tableWrapperRef}
                            onColumnDragEnd={handleColumnDragEnd}
                            onColumnDraggedOver={handleColumnDraggedOver}
                            onColumnDropped={handleColumnDropped}
                            onOrderChange={handleOrderChange}
                            onResizeEnd={(width: number) => handleResizeEnd(columnKey, width)}
                            order={order}
                            resizable={!isLastColumn && (meta?.resizable ?? true)}
                            rightAligned={meta?.alignment === 'right'}
                            sortKey={meta?.sortKey || columnKey}
                            sortable={meta?.sortable ?? true}
                            tableId={tableId}
                            width={widths[columnKey]}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHeaderCell>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
              <tbody className="bg-white">{renderTableBody()}</tbody>
            </table>
          </div>
          <TableColumnActionIndicator leftOffset={markerLeftOffset} visible={isMarkerVisible} />
        </div>
      </DndProvider>
    );
  };

  return (
    <div className={wrapperClassNames}>
      {showTableHeader && <TableHeader actions={actions} classNames={titleClassNames} tagline={tagline} title={title} />}
      {additionalActions && (
        <div className="px-6 py-3" data-testid="table-additional-actions">
          {additionalActions}
        </div>
      )}
      {renderTable()}
      {showTableFooter && <TableFooter paginationInfo={paginationInfo} totalCount={tableTotals} />}
    </div>
  );
};

export type { Props as TableProps };

export default Table;
